import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    DollarSign,
    Activity,
    Calendar,
    CreditCard,
    Download,
    CheckCircle2,
    XCircle,
    Clock
} from 'lucide-react';
import { collection, query, orderBy, limit, getDocs, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface BillingData {
    currentBalance: number;
    billingCycle: string;
    totalApiCalls: number;
    totalCost: number;
    costPerCall: number;
}

interface UsageLog {
    timestamp: Date;
    endpoint: string;
    success: boolean;
    cost: number;
    file_name?: string;
    error?: string;
}

export function Billing() {
    const { user } = useAuth();
    const [billing, setBilling] = useState<BillingData | null>(null);
    const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [paymentLoading, setPaymentLoading] = useState(false);

    useEffect(() => {
        if (user?.userId) {
            fetchBillingData();
            fetchUsageLogs();
        }
    }, [user?.userId]);

    const fetchBillingData = async () => {
        if (!user?.userId) return;

        try {
            const userDocRef = doc(db, 'bgusers', user.userId);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists()) {
                const data = userDocSnap.data();
                const billingData = data.billing || {};

                console.log('Billing data from Firestore:', billingData); // Debug log

                setBilling({
                    currentBalance: billingData.currentBalance || 0,
                    billingCycle: billingData.billingCycle || new Date().toISOString().slice(0, 7),
                    totalApiCalls: billingData.totalApiCalls || 0,
                    totalCost: billingData.totalCost || 0,
                    costPerCall: 0.02
                });
            }
        } catch (error) {
            console.error('Error fetching billing data:', error);
        }
    };

    const fetchUsageLogs = async () => {
        if (!user?.userId) return;

        try {
            setLoading(true);
            const usageRef = collection(db, 'bgusers', user.userId, 'api_usage');
            const q = query(usageRef, orderBy('timestamp', 'desc'), limit(50));
            const querySnapshot = await getDocs(q);

            const logs: UsageLog[] = [];
            querySnapshot.forEach((doc: any) => {
                const data = doc.data();
                logs.push({
                    timestamp: data.timestamp?.toDate() || new Date(),
                    endpoint: data.endpoint || '',
                    success: data.success || false,
                    cost: data.cost || 0.02,
                    file_name: data.file_name,
                    error: data.error
                });
            });

            setUsageLogs(logs);
        } catch (error) {
            console.error('Error fetching usage logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePayNow = async () => {
        if (!billing || billing.currentBalance <= 0) return;

        setPaymentLoading(true);

        try {
            const razorpayKey = import.meta.env.VITE_RAZORPAY_KEY_ID;

            if (!razorpayKey || razorpayKey === 'rzp_test_xxxxxxxx') {
                alert('Razorpay is not configured. Please add your Razorpay test key to .env file');
                setPaymentLoading(false);
                return;
            }

            const options = {
                key: razorpayKey,
                amount: Math.round(billing.currentBalance * 100), // Convert to paise
                currency: 'USD',
                name: 'BgRemover',
                description: `API Usage Bill - ${billing.billingCycle}`,
                handler: async function (response: any) {
                    // Payment successful
                    console.log('Payment successful:', response);
                    alert('Payment successful! Your balance has been cleared.');

                    // Refresh billing data
                    await fetchBillingData();
                    await fetchUsageLogs();
                },
                prefill: {
                    email: user?.email || '',
                },
                theme: {
                    color: '#000000'
                }
            };

            const rzp = new (window as any).Razorpay(options);
            rzp.open();
        } catch (error) {
            console.error('Payment error:', error);
            alert('Payment failed. Please try again.');
        } finally {
            setPaymentLoading(false);
        }
    };

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    if (!user?.apiKey) {
        return (
            <div className="min-h-screen bg-background py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center py-12">
                        <p className="text-muted-foreground">You need an API key to view billing information.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Credit pack users don't have pay-as-you-go billing
    if (user?.planType === 'credit_pack') {
        return (
            <div className="min-h-screen bg-background py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center py-12">
                        <div className="max-w-md mx-auto bg-card border border-border rounded-xl p-8">
                            <div className="p-3 bg-primary/10 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                <CreditCard className="h-8 w-8 text-primary" />
                            </div>
                            <h2 className="text-2xl font-bold text-foreground mb-2">Credit Pack Plan</h2>
                            <p className="text-muted-foreground mb-4">
                                You're on a prepaid credit pack plan. This billing page is only for Enterprise (Pay-as-you-go) users.
                            </p>
                            <p className="text-sm text-muted-foreground">
                                View your credit usage on the <a href="/dashboard" className="text-primary hover:underline">Dashboard</a>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Billing</h1>
                    <p className="text-muted-foreground">Manage your API usage and payments</p>
                </div>

                {/* Billing Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-card border border-border rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-red-500/10 rounded-lg">
                                <DollarSign className="h-6 w-6 text-red-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Current Balance</p>
                                <p className="text-2xl font-bold text-foreground">
                                    {formatCurrency(billing?.currentBalance || 0)}
                                </p>
                            </div>
                        </div>
                        {billing && billing.currentBalance > 0 && (
                            <button
                                onClick={handlePayNow}
                                disabled={paymentLoading}
                                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
                            >
                                {paymentLoading ? 'Processing...' : 'Pay Now'}
                            </button>
                        )}
                    </div>

                    <div className="bg-card border border-border rounded-xl p-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-500/10 rounded-lg">
                                <Activity className="h-6 w-6 text-blue-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total API Calls</p>
                                <p className="text-2xl font-bold text-foreground">
                                    {billing?.totalApiCalls || 0}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-xl p-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-green-500/10 rounded-lg">
                                <Calendar className="h-6 w-6 text-green-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Billing Cycle</p>
                                <p className="text-2xl font-bold text-foreground">
                                    {billing?.billingCycle || 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Usage Logs */}
                <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-foreground mb-1">Usage Logs</h2>
                            <p className="text-sm text-muted-foreground">
                                Detailed history of your API calls (${formatCurrency(billing?.costPerCall || 0.02)} per call)
                            </p>
                        </div>
                        <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                            <Download className="h-4 w-4" />
                            Export CSV
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-12 text-muted-foreground">Loading usage logs...</div>
                    ) : usageLogs.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            No API calls yet. Start using your API key to see usage logs.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-border">
                                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date & Time</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Endpoint</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">File</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usageLogs.map((log, index) => (
                                        <tr key={index} className="border-b border-border hover:bg-muted/50 transition-colors">
                                            <td className="py-3 px-4 text-sm text-foreground">
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                                    {formatDate(log.timestamp)}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-sm text-foreground font-mono">
                                                {log.endpoint}
                                            </td>
                                            <td className="py-3 px-4 text-sm text-muted-foreground truncate max-w-xs">
                                                {log.file_name || 'N/A'}
                                            </td>
                                            <td className="py-3 px-4">
                                                {log.success ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/10 text-green-500 text-xs font-medium">
                                                        <CheckCircle2 className="h-3 w-3" />
                                                        Success
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 text-red-500 text-xs font-medium">
                                                        <XCircle className="h-3 w-3" />
                                                        Failed
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-sm font-medium text-foreground text-right">
                                                {formatCurrency(log.cost)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
